import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import { fetchBranches } from "@/api/branches";
import { createStudent, updateStudent } from "@/api/students";
import { extractErrorMessage } from "@/types/common";
import { LEAD_SOURCE_LABELS } from "@/types/enums";
import type { LeadSource } from "@/types/enums";
import type { Student } from "@/types/student";

const schema = z.object({
  fullName: z.string().min(2, "Введите ФИО"),
  phone: z.string().min(9, "Введите номер телефона"),
  parentPhone: z.string().optional(),
  birthYear: z.coerce.number().min(1950).max(new Date().getFullYear()),
  branchId: z.string().min(1, "Выберите филиал"),
  source: z.string().min(1, "Выберите источник"),
  note: z.string().optional(),
});
type FormValues = z.infer<typeof schema>;

interface StudentCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Если передан — диалог работает в режиме редактирования существующего ученика. */
  student?: Student | null;
}

export function StudentCreateDialog({ open, onClose, student }: StudentCreateDialogProps) {
  const queryClient = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);
  const { data: branches } = useQuery({ queryKey: ["branches"], queryFn: fetchBranches });
  const isEdit = Boolean(student);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: student
      ? {
          fullName: student.fullName,
          phone: student.phone,
          parentPhone: student.parentPhone ?? "",
          birthYear: student.birthYear,
          branchId: student.branchId,
          source: student.source,
          note: student.note ?? "",
        }
      : undefined,
    defaultValues: { source: "OTHER" },
  });

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      const payload = {
        fullName: values.fullName,
        phone: values.phone,
        parentPhone: values.parentPhone || undefined,
        birthYear: values.birthYear,
        branchId: values.branchId,
        source: values.source as LeadSource,
        note: values.note || undefined,
      };
      return isEdit ? updateStudent(student!.id, payload) : createStudent({ ...payload, status: "ACTIVE" });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["students"] });
      void queryClient.invalidateQueries({ queryKey: ["student", student?.id] });
      reset();
      onClose();
    },
    onError: (error) => setServerError(extractErrorMessage(error)),
  });

  function onSubmit(values: FormValues) {
    setServerError(null);
    mutation.mutate(values);
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "Редактировать ученика" : "Новый ученик"}</DialogTitle>
      <DialogContent>
        {serverError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {serverError}
          </Alert>
        )}
        <Grid container spacing={2} mt={0.5}>
          <Grid item xs={12}>
            <TextField
              label="ФИО"
              fullWidth
              {...register("fullName")}
              error={Boolean(errors.fullName)}
              helperText={errors.fullName?.message}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Телефон"
              fullWidth
              {...register("phone")}
              error={Boolean(errors.phone)}
              helperText={errors.phone?.message}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Телефон родителя" fullWidth {...register("parentPhone")} />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Год рождения"
              type="number"
              fullWidth
              {...register("birthYear")}
              error={Boolean(errors.birthYear)}
              helperText={errors.birthYear?.message}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              select
              label="Филиал"
              fullWidth
              defaultValue=""
              {...register("branchId")}
              error={Boolean(errors.branchId)}
              helperText={errors.branchId?.message}
            >
              {(branches ?? []).map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField select label="Источник" fullWidth defaultValue="OTHER" {...register("source")}>
              {Object.entries(LEAD_SOURCE_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField label="Заметка" fullWidth multiline minRows={2} {...register("note")} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Отмена
        </Button>
        <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
          {isEdit ? "Сохранить" : "Создать"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
